import type {
  PeachIdTable,
  ResourceCatalog,
} from 'core/helpers/peachIdLink/types'
import {
  resourceCatalog,
  type ResourceCatalogEntry,
} from 'core/types/generated/resources'

/**
 * The only file in the app that knows Peach-ID link URLs, alongside
 * `resourcePaths.ts` and under the same sc-76790 routing discipline: mounts and
 * links never compose path strings themselves.
 *
 * A key listed here is linkable. Keys are opt-in because there is no registry
 * of mounted routes to check a derived path against, so an unlisted resource
 * renders as plain text rather than risking a dead link.
 */
const LINKABLE: Array<string> = [
  'appDomains',
  'caseTypes',
  'cases',
  'companies',
  'documents',
  'employees',
  'loanTypes',
  'loans',
  'people',
  'roles',
  'supercases',
  'teams',
  'transactions',
  'users',
]

/** The API nests these differently from the UI, so the whole template is given. */
const PATH_OVERRIDES: { [key: string]: string } = {
  cases: '/companies/:companyId/borrowers/:personId/cases/:id',
  documents: '/companies/:companyId/borrowers/:personId/documents/:id',
  loans: '/companies/:companyId/borrowers/:personId/loans/:id',
}

/** API segment to UI segment. The API says people; the UI says borrowers. */
const SEGMENT_OVERRIDES: { [segment: string]: string } = {
  people: 'borrowers',
}

/**
 * Catalog key to UI slug, for keys whose UI slug diverges outright. Empty for
 * now: the only known candidate is `keys`, which is deliberately not linkable
 * (see Out of scope).
 */
const SLUG_OVERRIDES: { [key: string]: string } = {}

const toKebab = (segment: string): string =>
  segment.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()

/**
 * Turns a catalog path into a UI template: `{param}` becomes `:param`, segments
 * are kebab-cased and remapped, and the resource's own id is appended.
 */
export const deriveTemplate = (entry: ResourceCatalogEntry): string => {
  const segments = entry.path
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      if (segment.startsWith('{')) return `:${segment.slice(1, -1)}`
      const kebab = toKebab(segment)
      return SEGMENT_OVERRIDES[segment] ?? SEGMENT_OVERRIDES[kebab] ?? kebab
    })

  const last = segments[segments.length - 1]
  if (last && SLUG_OVERRIDES[entry.key]) {
    segments[segments.length - 1] = SLUG_OVERRIDES[entry.key]
  }

  return `/${segments.join('/')}/:id`
}

export const buildPeachIdTable = (
  catalog: ResourceCatalog = resourceCatalog,
): PeachIdTable => {
  const templates: { [key: string]: string } = {}

  for (const key of LINKABLE) {
    // An override still requires the resource to exist, so a resource renamed
    // or dropped from the spec stops linking instead of keeping a stale URL.
    const entry = catalog[key]
    if (!entry) continue
    templates[key] = PATH_OVERRIDES[key] ?? deriveTemplate(entry)
  }

  // Prefix to resource is the one mapping no generated artifact carries, so it
  // stays opt-in. Add an entry only when a real case appears.
  return { byPrefix: {}, templates }
}

export const devToolsPeachIdRoutes: PeachIdTable = buildPeachIdTable()
